// GrabCAD module — data export builder, window-message TX, file save, and
// file import RX.
//
// TX (importAssembly) and saveFile are request/response: each outgoing message
// carries an id and the function awaits the matching response. The file
// import RX flow has no correlation id — its messages are discriminated by
// their `info` field, so only one import may be in flight at a time.

import { GrabCADExport, GCMeshPart, GCFileDep, ExportOptions } from './GrabCADTypes.ts';
import { isMeshPart } from '../_utils.ts';
import { FileData, FileExportArray, FileIdx, ValidFilename } from '../../../data/data-interfaces.internal.ts';
import { devlog } from '../../../logic/utils.ts';
import { isProduction } from '../../../data/constants.ts';
import { unzipSync, strFromU8 } from 'fflate';

// ============================================================================
// Validation
// ============================================================================

export interface ValidationError {
  fileKey: string;
  fileName?: string;
  message: string;
}

export interface ValidationResult {
  valid: boolean;
  errors: ValidationError[];
}

export function validateForGrabCAD(files: FileExportArray): ValidationResult {
  const errors: ValidationError[] = [];
  for (const file of files) {
    if (!isMeshPart(file)) continue;
    if (file.fileType !== '3mf') {
      errors.push({
        fileKey: file.fileKey,
        fileName: file.fileName,
        message: `fileType is "${file.fileType}" — GrabCAD requires "3mf"`,
      });
    }
    if (file.data.byteLength === 0) {
      errors.push({
        fileKey: file.fileKey,
        fileName: file.fileName,
        message: 'data is empty',
      });
    }
  }
  return { valid: errors.length === 0, errors };
}

// ============================================================================
// buildGrabCADExport — pure transformer from populated FileExportArray
// ============================================================================

function defaultURN(file: FileData): string {
  return `${file.appSource}:${file.category}:${file.fileKey}`;
}

export async function buildGrabCADExport(
  files: FileExportArray,
  opts: ExportOptions = {}
): Promise<GrabCADExport> {
  const exported: GrabCADExport = {
    name: opts.packageName ?? '',
    parts: [],
    dependencies: []
  };

  const getURN = opts.getURN ?? defaultURN;
  const partMap = new Map<FileIdx, GCMeshPart>();

  for (const file of files) {
    if (!isMeshPart(file)) continue;

    const data = file.data;

    const part: GCMeshPart = {
      name: (file.fileName || file.fileKey) as ValidFilename,
      data,
      extension: '3mf',
      suppress: file.category === 'workpiece',
      URN: getURN(file),
      dependencies: []
    };

    exported.parts.push(part);
    partMap.set(file.fileIdx, part);
  }

  for (const file of files) {
    if (isMeshPart(file)) continue;

    const dep: GCFileDep = {
      name: (file.fileName || file.fileKey) as ValidFilename,
      extension: file.fileType,
      data: file.data,
      URN: getURN(file)
    };

    const parentPart = file.parent !== undefined ? partMap.get(file.parent) : null;
    if (parentPart) {
      parentPart.dependencies.push(dep);
    } else {
      exported.dependencies.push(dep);
    }

  }

  return exported;
}

// ============================================================================
// Shared messaging infrastructure
// ============================================================================

export interface GrabCadOptions {
  /** Window to send messages to (default: window.opener). */
  target?: Window;
  /** postMessage targetOrigin (default: '*'). */
  targetOrigin?: string;
  /**
   * EventTarget to listen on for response messages (default: window).
   * Override in tests or non-window contexts.
   */
  listenOn?: EventTarget;
}

function resolveOpts(opts: GrabCadOptions) {
  return {
    target: opts.target ?? window.opener as Window,
    origin: opts.targetOrigin ?? '*',
    listenOn: opts.listenOn ?? window as EventTarget,
  };
}

// ============================================================================
// TX — transmit a GrabCADExport package to GrabCAD
// ============================================================================

/**
 * Dev diagnostic only: count geometry inside a 3MF buffer (a zip whose mesh lives
 * in a `*.model` XML entry). Returns null if the buffer isn't a parseable 3MF.
 * triangles=0 means an empty-geometry mesh — the kind GrabCAD's importer chokes on.
 */
function count3mfGeometry(data: ArrayBuffer): { objects: number; triangles: number; vertices: number } | null {
  try {
    const files = unzipSync(new Uint8Array(data));
    // 3MF geometry may be split across several *.model parts (production extension),
    // so sum over all of them rather than trusting the first.
    const modelPaths = Object.keys(files).filter((p) => p.toLowerCase().endsWith('.model'));
    if (modelPaths.length === 0) return null;
    const totals = { objects: 0, triangles: 0, vertices: 0 };
    for (const path of modelPaths) {
      const xml = strFromU8(files[path]);
      totals.objects   += (xml.match(/<object\b/g)   || []).length;
      totals.triangles += (xml.match(/<triangle\b/g) || []).length;
      totals.vertices  += (xml.match(/<vertex\b/g)   || []).length;
    }
    return totals;
  } catch {
    return null;
  }
}

/**
 * Transmits a GrabCADExport to GrabCAD in a single postMessage call.
 * ArrayBuffers are transferred — the pkg buffers are neutered after this call.
 * Returns the generated assembly ID.
 *
 *   → {command: 'importAssembly', id, data: GrabCADExport}
 *   ← {info: 'assemblyFinalized', id}  — success
 *   ← {info: 'assemblyError', error}   — failure
 */
export async function grabCadTX(pkg: GrabCADExport, opts: GrabCadOptions = {}): Promise<string> {
  const { target, origin, listenOn } = resolveOpts(opts);
  const id = crypto.randomUUID();

  const transfers: ArrayBuffer[] = [];
  for (const part of pkg.parts) {
    if (part.data.byteLength > 0) transfers.push(part.data);
    for (const dep of part.dependencies) {
      if (dep.data.byteLength > 0) transfers.push(dep.data);
    }
  }
  for (const dep of pkg.dependencies) {
    if (dep.data.byteLength > 0) transfers.push(dep.data);
  }

  // Non-production diagnostic: log the assembly contents before the buffers are
  // transferred (and thus neutered) by postMessage. A part with a near-empty
  // byteLength is a degenerate/empty mesh that GrabCAD's importer can choke on.
  if (!isProduction) {
    console.log(
      `[grabCadTX] importAssembly "${pkg.name}" — ${pkg.parts.length} part(s), ` +
      `${pkg.dependencies.length} top-level dep(s), id=${id}`
    );
    const geomCols = (ext: string, data: ArrayBuffer) => {
      const g = ext === '3mf' ? count3mfGeometry(data) : null;
      return { triangles: g ? g.triangles : '', objects: g ? g.objects : '' };
    };
    console.table([
      ...pkg.parts.map((p) => ({
        kind: 'part', name: p.name, ext: p.extension, bytes: p.data.byteLength,
        ...geomCols(p.extension, p.data),
        suppress: p.suppress, deps: p.dependencies.length, URN: p.URN,
      })),
      ...pkg.parts.flatMap((p) => p.dependencies.map((d) => ({
        kind: 'part-dep', name: d.name, ext: d.extension, bytes: d.data.byteLength,
        ...geomCols(d.extension, d.data),
        suppress: '', deps: '', URN: d.URN,
      }))),
      ...pkg.dependencies.map((d) => ({
        kind: 'dep', name: d.name, ext: d.extension, bytes: d.data.byteLength,
        ...geomCols(d.extension, d.data),
        suppress: '', deps: '', URN: d.URN,
      })),
    ]);
  }

  await new Promise<void>((resolve, reject) => {
    function onMessage(event: MessageEvent): void {
      const d = event.data;
      if (d?.info === 'assemblyFinalized' && d?.id === id) {
        listenOn.removeEventListener('message', onMessage);
        resolve();
      } else if (d?.info === 'assemblyError') {
        listenOn.removeEventListener('message', onMessage);
        reject(new Error(d?.error ?? 'GrabCAD assembly error'));
      }
    }
    listenOn.addEventListener('message', onMessage);
    target.postMessage({ command: 'importAssembly', id, data: pkg }, origin, transfers);
  });

  return id;
}

// ============================================================================
// Save file — trigger GrabCAD/Electron's native save dialog
// ============================================================================

/** Result of a grabCadSaveFile call. */
export interface GrabCadSaveResult {
  /** False when the user cancelled the save dialog. */
  success: boolean;
  /** Path the file was written to (present only on success). */
  filepath?: string;
}

export interface GrabCadSaveFileOptions extends GrabCadOptions {
  /** Native dialog title (default 'Save File'). */
  title?: string;
  /** Extension shown in the dialog's file filter, without the dot
   *  (default: the filename's extension). */
  fileExtension?: string;
  /** Human-readable name for the extension filter (default '<EXT> File'). */
  fileExtensionName?: string;
}

/**
 * Asks GrabCAD to show a native save dialog and write the given blob to disk.
 * Resolves once GrabCAD reports the result — await this before closing the
 * window. The blob's ArrayBuffer is transferred with the request.
 *
 * Messages:
 *   → {command: 'saveFile', id, data: {filename, data, title, fileExtension, fileExtensionName}}
 *   ← {info: 'saveFileResult', id, success: true, filepath}   — saved
 *   ← {info: 'saveFileResult', id, success: false}            — user cancelled
 */
export async function grabCadSaveFile(
  blob: Blob,
  filename: string,
  opts: GrabCadSaveFileOptions = {}
): Promise<GrabCadSaveResult> {
  const { target, origin, listenOn } = resolveOpts(opts);
  const id = crypto.randomUUID();
  const data = await blob.arrayBuffer();
  const fileExtension = opts.fileExtension ?? filename.split('.').pop() ?? '';

  return new Promise<GrabCadSaveResult>((resolve) => {
    function onMessage(event: MessageEvent): void {
      const d = event.data;
      if (d?.info !== 'saveFileResult') return;
      devlog('[GrabCAD saveFile] saveFileResult received:', { receivedId: d?.id, expectedId: id, success: d?.success, filepath: d?.filepath });
      // Accept matching id, or an id-less response (GrabCAD may omit the id on cancel)
      if (d?.id !== undefined && d?.id !== id) return;
      listenOn.removeEventListener('message', onMessage);
      resolve(d.success ? { success: true, filepath: String(d.filepath) } : { success: false });
    }
    listenOn.addEventListener('message', onMessage);
    devlog('[GrabCAD saveFile] sending saveFile, id:', id, 'filename:', filename);
    target.postMessage({
      command: 'saveFile',
      id,
      data: {
        filename,
        data,
        title: opts.title ?? 'Save File',
        fileExtension,
        fileExtensionName: opts.fileExtensionName ?? `${fileExtension.toUpperCase()} File`,
      },
    }, origin);
  });
}

// ============================================================================
// RX — import a file from GrabCAD
// ============================================================================
//
// Protocol (responses are discriminated by their `info` field; the newer host
// adds an `id` to progress/error/abort messages, but openFileData still has
// none — so only one import flow may be in flight at a time):
//
//   → {command: 'openFile', multifile}
//   ← {info: 'openFileResult', files: [<string>]}             (empty when dismissed)
//       (legacy hosts: {info: 'openFileResult', filepath} — still accepted)
//
//   → {command: 'convertAndOpenFile', filepath, ...settings}  (settings.unit for STL,
//       plus optional repair / tessellationQuality / targetTriangleCount / detectClashes)
//   ← {info: 'openFileProgress', progress}                    (repeated; newer hosts send
//       stage strings 'converting' / 'reading', legacy hosts a number)
//   ← {info: 'openFileData', filename, filetype, data}        (success)
//   ← {info: 'openFileError', id?, error}                     (failure)
//   ← {info: 'openFileAborted', id?}                          (after abortConvert)
//
//   → {command: 'abortConvert', id?}

export type GrabCadStlUnit = 'mm' | 'cm' | 'in' | 'm' | 'ft';

/** Conversion progress: newer hosts report stage strings ('converting',
 *  'reading'), legacy hosts a number. */
export type GrabCadImportProgress = string | number;

/** Conversion settings sent alongside convertAndOpenFile. */
export interface GrabCadConvertSettings {
  /** Interpretation unit for unitless formats (currently only STL). */
  unit?: GrabCadStlUnit;
  repair?: boolean;
  tessellationQuality?: 'lowQuality' | 'highQuality';
  targetTriangleCount?: number;
  detectClashes?: boolean;
}

/** A file opened and converted by GrabCAD, ready for app consumption. */
export interface GrabCadImportedFile {
  filename: string;
  filetype: string;
  /** Converted file data. */
  data: ArrayBuffer;
}

export interface GrabCadImportOptions extends GrabCadOptions {
  /** Unit applied when the chosen file is an STL (default 'mm'). */
  unit?: GrabCadStlUnit;
  /** Allow selecting several files in the open dialog (default false). */
  multifile?: boolean;
  /** Extra conversion settings forwarded to every convertAndOpenFile call
   *  (unit is handled separately, per file). */
  convertSettings?: Omit<GrabCadConvertSettings, 'unit'>;
  /** Called for each openFileProgress message during conversion. */
  onProgress?: (progress: GrabCadImportProgress) => void;
}

/**
 * Asks GrabCAD to show its native open-file dialog.
 * Resolves with the chosen filepaths (a single entry unless opts.multifile),
 * or null if the dialog was dismissed. Parses the current protocol's
 * `files: [<string>]` result and falls back to the legacy `filepath` field
 * for older hosts. Pass each filepath on to grabCadConvertAndOpenFile to
 * receive the converted data.
 */
export async function grabCadOpenFile(opts: GrabCadOptions & { multifile?: boolean } = {}): Promise<string[] | null> {
  const { target, origin, listenOn } = resolveOpts(opts);

  return new Promise<string[] | null>((resolve) => {
    function onMessage(event: MessageEvent): void {
      const d = event.data;
      if (d?.info !== 'openFileResult') return;
      listenOn.removeEventListener('message', onMessage);
      const files: string[] = Array.isArray(d.files)
        ? d.files.filter((f: unknown): f is string => typeof f === 'string' && f.length > 0)
        : d.filepath ? [String(d.filepath)] : [];
      resolve(files.length ? files : null);
    }
    listenOn.addEventListener('message', onMessage);
    target.postMessage({ command: 'openFile', multifile: !!opts.multifile }, origin);
  });
}

/**
 * Asks GrabCAD to load and convert the file previously chosen via
 * grabCadOpenFile. Progress messages are forwarded to opts.onProgress.
 * Resolves with the converted file, or null if the conversion was aborted
 * (via grabCadAbortConvert). Rejects on openFileError.
 */
export async function grabCadConvertAndOpenFile(
  filepath: string,
  settings: GrabCadConvertSettings = {},
  opts: GrabCadOptions & { onProgress?: (progress: GrabCadImportProgress) => void } = {}
): Promise<GrabCadImportedFile | null> {
  const { target, origin, listenOn } = resolveOpts(opts);

  return new Promise<GrabCadImportedFile | null>((resolve, reject) => {
    function finish(settle: () => void): void {
      listenOn.removeEventListener('message', onMessage);
      settle();
    }
    function onMessage(event: MessageEvent): void {
      const d = event.data;
      switch (d?.info) {
        case 'openFileProgress':
          opts.onProgress?.(d.progress);
          break;
        case 'openFileData':
          finish(() => resolve({ filename: d.filename, filetype: d.filetype, data: d.data }));
          break;
        case 'openFileError':
          finish(() => reject(new Error(d.error ?? 'GrabCAD file conversion error')));
          break;
        case 'openFileAborted':
          finish(() => resolve(null));
          break;
      }
    }
    listenOn.addEventListener('message', onMessage);
    target.postMessage({ command: 'convertAndOpenFile', filepath, ...settings }, origin);
  });
}

/**
 * Asks GrabCAD to abort an in-flight conversion. GrabCAD answers with
 * openFileAborted, which resolves the pending grabCadConvertAndOpenFile to null.
 */
export function grabCadAbortConvert(opts: GrabCadOptions = {}): void {
  const { target, origin } = resolveOpts(opts);
  target.postMessage({ command: 'abortConvert' }, origin);
}

/**
 * OS path of a File dropped inside GrabCAD's Electron host (File.path).
 * Null for synthetic blobs or hosts that don't expose a path.
 */
export function grabCadDroppedFilePath(blob: Blob): string | null {
  const path = (blob as { path?: unknown }).path;
  return typeof path === 'string' && path.length > 0 ? path : null;
}

/**
 * Converts a file whose path is already known — skips the open-file dialog
 * (used for files dropped onto the app, where the OS path comes with the drop).
 * For STL files the unit setting is included (default 'mm'). Resolves with the
 * converted file, or null if the conversion was aborted.
 */
export async function grabCadConvertFile(
  filepath: string,
  opts: GrabCadImportOptions = {}
): Promise<GrabCadImportedFile | null> {
  const { unit, multifile, convertSettings, onProgress, ...gcOpts } = opts;

  const settings: GrabCadConvertSettings = { ...convertSettings };
  if (filepath.split('.').pop()?.toUpperCase() === 'STL') settings.unit = unit ?? 'mm';

  return grabCadConvertAndOpenFile(filepath, settings, { ...gcOpts, onProgress });
}

/**
 * Converts several files sequentially (openFileData carries no correlation id,
 * so conversions cannot overlap). An aborted conversion stops the remainder of
 * the batch; files converted before the abort are still returned. Resolves
 * null when nothing was converted.
 */
export async function grabCadConvertFiles(
  filepaths: string[],
  opts: GrabCadImportOptions = {}
): Promise<GrabCadImportedFile[] | null> {
  const results: GrabCadImportedFile[] = [];
  for (const filepath of filepaths) {
    const file = await grabCadConvertFile(filepath, opts);
    if (!file) break; // aborted — don't start the remaining conversions
    results.push(file);
  }
  return results.length ? results : null;
}

/**
 * Full import flow: open-file dialog (multi-select when opts.multifile), then
 * convert each chosen file sequentially. Resolves with the converted files,
 * or null if the user cancelled the dialog or the (first) conversion was
 * aborted.
 */
export async function grabCadImportFile(opts: GrabCadImportOptions = {}): Promise<GrabCadImportedFile[] | null> {
  const filepaths = await grabCadOpenFile(opts);
  if (!filepaths) return null;
  return grabCadConvertFiles(filepaths, opts);
}
